import type { MasterProductSummary } from 'apis/restaurant-inquiries';

type UnitFields = Pick<MasterProductSummary, 'baseUnit' | 'itemUnit' | 'netContent' | 'packSize' | 'packUnit'>;

// Naive pluralizer for unit words the catalog uses (pack → packs, box → boxes,
// tray → trays); only applied when the count isn't 1. Exported so the inline
// fill-in-the-blank editors (variant-elimination-fields, inline-variant-form)
// can pluralize their unit dropdown labels live, matching this sentence.
export const plural = (word: string, count: number): string => {
  if (count === 1) return word;
  // Measurement abbreviations (kg, oz, l, g, mg, ml, lb) don't pluralize in English —
  // "5 kg", never "5 kgs". Safe only because no container word in the vocabulary served
  // by GET cms/master-products/uom-options is this short; the shortest ('box') is 3
  // characters, leaving a one-character margin.
  if (word.length <= 2) return word;
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
};

// 0.7 * 10 = 6.999999… — keep totals human.
export const round = (n: number): number => Math.round(n * 1000) / 1000;

// One sentence explaining how the product is packed, instead of the old
// "UOM kg · size 5 kg / pack · pack 1 × carton" chip soup. Field semantics
// (master-products module): baseUnit = fundamental unit everything is measured
// in (kg); itemUnit = the sellable unit an item is divided into (pack, piece);
// netContent = how much baseUnit one itemUnit holds (5 kg); packUnit = the
// outer package (carton); packSize = how many itemUnits per package.
// Examples:
//   full data          → "1 carton contains 5 packs of 5 kg each — 25 kg per carton."
//   packSize = 1       → "1 carton contains 1 pack of 5 kg."
//   no pack level      → "Each pack contains 5 kg."
//   only baseUnit      → "Measured in kg."
// Returns null when nothing is known (the panel renders its explicit '—').
export function describeUnits(mp?: UnitFields | null): string | null {
  const base = mp?.baseUnit?.trim() || '';
  const item = mp?.itemUnit?.trim() || '';
  const pack = mp?.packUnit?.trim() || '';
  const netContent = mp?.netContent ?? null;
  const packSize = mp?.packSize ?? null;

  const itemWord = item || 'unit';
  const itemContent = netContent != null && base ? `${netContent} ${base}` : '';

  if (pack && packSize != null) {
    const items = `${packSize} ${plural(itemWord, packSize)}`;
    if (itemContent && netContent != null) {
      if (packSize === 1) return `1 ${pack} contains ${items} of ${itemContent}.`;
      return `1 ${pack} contains ${items} of ${itemContent} each — ${round(netContent * packSize)} ${base} per ${pack}.`;
    }
    return `1 ${pack} contains ${items}.`;
  }
  if (pack && itemContent) return `Packed in ${plural(pack, 2)}; each ${itemWord} contains ${itemContent}.`;
  if (itemContent) return `Each ${itemWord} contains ${itemContent}.`;
  if (base) return `Measured in ${base}.`;
  if (pack) return `Packed in ${plural(pack, 2)}.`;
  return null;
}
